// What the membership gives, in the order the room lists it, and the small
// picture each benefit carries. One table serves the paywall in the room
// (src/ui/account.ts draws the SVG inline) and the membership page on the
// laptop (scripts/build-desktop.ts writes each picture as a file beside the
// Settings app, and the PocketJS build bakes it into the app's pack).
//
// The pictures share one language: a 24 unit box, flat fills from the room's
// palette, a 1.5 unit keyline in the room's ink and soft corners. A line
// that stands alone (an antenna, a shelf) is lilac, which reads on the
// room's dark cards and on the laptop's light windows alike.
// The English text is ASCII: the laptop's faces were baked with nothing
// else. PERK_TEXT holds each benefit's words in every language, here and not
// in the site's catalogs, so the laptop's Settings app takes these lines and
// not every sentence of the site; `perkText` and `perkLine` read it.

import type { Lang } from "./lang.ts";

export type PerkId = "versions" | "tv" | "colours" | "rooms" | "link";

export interface Perk {
  id: PerkId;
  text: string;
  /** Promised, not there yet: the lists say so after the text. */
  soon?: boolean;
}

export const PERKS: readonly Perk[] = [
  // (a game's current packages are free for every account: the membership adds the ones before them)
  { id: "versions", text: "Every past version of a game's packages" },
  { id: "colours", text: "Every device colour, and a palette to mix your own" },
  { id: "tv", text: "Your games on Pocket TV and in search" },
  { id: "rooms", text: "More rooms", soon: true },
  { id: "link", text: "Play between devices", soon: true },
];

export const SOON = "coming soon";

/** Each benefit's text, and what follows the text of one that is to come, by language. English is PERKS'. */
export const PERK_TEXT: Record<Lang, Record<PerkId | "soon", string>> = {
  en: {
    ...(Object.fromEntries(PERKS.map((perk) => [perk.id, perk.text])) as Record<PerkId, string>),
    soon: `, ${SOON}`,
  },
  ja: {
    versions: "ゲームのパッケージの過去のバージョンすべて",
    colours: "デバイスのすべての色と、自分の色を作るパレット",
    tv: "あなたのゲームを Pocket TV と検索に表示",
    rooms: "ほかの部屋",
    link: "デバイス同士で遊ぶ",
    soon: "（近日公開）",
  },
};

/** A benefit's text in `lang`. */
export function perkText(id: PerkId, lang: Lang): string {
  return PERK_TEXT[lang][id];
}

/** What follows the text of a benefit that is to come, in `lang`: `, coming soon`, `（近日公開）`. */
export function soonText(lang: Lang): string {
  return PERK_TEXT[lang].soon;
}

/** A benefit as one line in `lang`: its text, and for one that is to come, what says so. */
export function perkLine(perk: Perk, lang: Lang): string {
  return perk.soon ? perkText(perk.id, lang) + soonText(lang) : perkText(perk.id, lang);
}

const INK = "#171226";
/** How far a fill sits inside its keyline. */
const EDGE = 1.5;
const n = (value: number) => +value.toFixed(2);

// Every shape is a fill. A keyline is the same shape in ink behind a smaller
// one in colour, and a line is a thin rounded bar: the pack builder that
// bakes these for the laptop draws no strokes.
const rect = (x: number, y: number, w: number, h: number, r: number, fill: string) =>
  `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}" rx="${n(r)}" fill="${fill}"/>`;
const box = (x: number, y: number, w: number, h: number, r: number, fill: string, edge = EDGE) =>
  rect(x, y, w, h, r, INK) + rect(x + edge, y + edge, w - edge * 2, h - edge * 2, Math.max(0.4, r - edge), fill);
const dot = (cx: number, cy: number, r: number, fill: string) => `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}" fill="${fill}"/>`;
const disc = (cx: number, cy: number, r: number, fill: string) => dot(cx, cy, r, INK) + dot(cx, cy, r - EDGE, fill);
const shape = (d: string, fill: string) => `<path d="${d}" fill="${fill}"/>`;
/** A spark of four points: tips `tip` from the centre, waists `waist` from it on the diagonals. */
const spark = (cx: number, cy: number, tip: number, waist: number, fill: string) => {
  const w = waist * Math.SQRT1_2;
  const points = [[0, -tip], [w, -w], [tip, 0], [w, w], [0, tip], [-w, w], [-tip, 0], [-w, -w]];
  return shape(`M${points.map(([x, y]) => `${n(cx + x!)} ${n(cy + y!)}`).join("L")}z`, fill);
};

const DRAWN: Record<PerkId, string> = {
  // Two cases of one game, the older behind the newer, and the arrow into the newer one.
  versions:
    box(1.6, 1.4, 14.4, 13.6, 3.2, "#a98bff") +
    box(6.4, 5.6, 16, 15, 3.4, "#ffb45c") +
    rect(13.2, 8.6, 2.4, 5.4, 1, INK) + shape("M9.9 13h9l-4.5 5z", INK),
  // The room's television: two antennae, a cream set, a lit picture, two dials.
  tv:
    shape("M7.2 2.4l1.5-1.4L12 4.2 15.3 1l1.5 1.4L12 7.2z", "#a98bff") +
    box(2.6, 6.2, 18.8, 15, 3.8, "#fcf6ff") +
    box(5.2, 8.9, 10.2, 9.6, 2.4, "#3fd0e8", 1.3) +
    dot(18.1, 11.3, 1.4, "#ff5f9e") + dot(18.1, 15.9, 1.4, "#ffd23f"),
  // Three pots of paint, overlapping.
  colours: disc(8.2, 8.4, 6, "#ff5f9e") + disc(15.8, 8.4, 6, "#ffd23f") + disc(12, 15.4, 6, "#3fd0e8"),
  // A second room: a wall, a door, a roof.
  rooms:
    box(4.6, 9.8, 14.8, 11.8, 2, "#fcf6ff") +
    rect(9.4, 13.4, 5.2, 8.2, 1.4, INK) + rect(10.7, 14.7, 2.6, 5.6, 0.7, "#ffd23f") +
    shape("M1 12.4L12 1.8l11 10.6z", INK) + shape("M4.9 10.8L12 4l7.1 6.8z", "#ff5f9e"),
  // Two handhelds and the signal between them.
  link:
    dot(8.9, 4.5, 1.25, "#ffd23f") + dot(12, 3.2, 1.25, "#ffd23f") + dot(15.1, 4.5, 1.25, "#ffd23f") +
    box(1.4, 7.4, 9.4, 14, 2.8, "#3fd0e8") + box(13.2, 7.4, 9.4, 14, 2.8, "#a98bff") +
    rect(3.9, 10, 4.4, 4.4, 1, INK) + rect(15.7, 10, 4.4, 4.4, 1, INK) +
    dot(6.1, 17.7, 1.1, INK) + dot(17.9, 17.7, 1.1, INK),
};

/** A benefit's picture as an SVG document, `size` pixels square. */
export function perkSvg(id: PerkId, size = 24): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">${DRAWN[id]}</svg>`;
}
